import React, { useState, useRef } from 'react';
import {
  Box, Stack, Typography, TextField, Switch, Autocomplete, MenuItem,
} from '@mui/material';

// สไตล์เดียวกับ EDITABLE_BORDER ใน frontend/src/views/Project/projectSingleDashboard/constants.js
// (กรอบเส้นบางสีจาง = สัญลักษณ์บอกว่าช่องนี้แก้ไขได้ แทนไอคอนปากกา) — ไม่ import ข้ามโมดูล
// เพราะฟิลด์ที่นี่เป็น text/relation เป็นหลัก ต่างจาก Row ของ Project ที่เน้นตัวเลขการเงิน
const EDITABLE_BORDER = '1px solid #cbd5e1';
const LABEL_WIDTH = 150;

const valueBoxSx = (disabled) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: EDITABLE_BORDER,
  borderRadius: 0.75,
  px: 0.75,
  py: 0.3,
  opacity: disabled ? 0.6 : 1,
  '&:hover': disabled ? {} : { borderColor: '#94a3b8', bgcolor: 'action.hover' },
});

// แถวอ่านอย่างเดียว — ไม่มีกรอบ (แก้ไขไม่ได้ในแถวนี้)
export function Row({
  label, value, bold, color, chip,
}) {
  return (
    <Box sx={{ py: 0.4 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ width: LABEL_WIDTH, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280' }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', px: 0.75, py: 0.3 }}>
          {chip || (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: bold ? 700 : 400,
                color: color || 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {value ?? '-'}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// แถวแก้ไขได้ — คลิกค่าเพื่อเปิดช่องกรอก, blur/Enter = บันทึกทันที
// (type: text/textarea/number/date/boolean/select — select ต้องใส่ options: [{value,label}])
export function EditableRow({
  label, value, displayValue, onSave, type = 'text', disabled, options, clearOnEdit,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef();
  const cancelledRef = useRef(false);
  const doneRef = useRef(false);

  const startEdit = () => {
    if (disabled) return;
    cancelledRef.current = false;
    doneRef.current = false;
    // clearOnEdit: field ที่มีประวัติแยกเก็บทุกครั้งที่บันทึกอยู่แล้ว (เช่น Note — ดู
    // marketing_note_history) — เปิดแก้แต่ละครั้งคือ "เพิ่มรายการใหม่" ไม่ใช่แก้ของเก่า
    // เริ่มจากค่าว่างเสมอ กันพิมพ์ทับ note เก่าเงียบๆ โดยไม่ตั้งใจ
    setDraft(clearOnEdit ? '' : (value ?? ''));
    setEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 50);
  };

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (type === 'number') {
      const trimmed = String(draft).trim();
      const next = trimmed === '' ? null : Number(trimmed);
      if (next !== null && Number.isNaN(next)) return;
      onSave(next);
    } else if (clearOnEdit && draft === '') {
      // clearOnEdit เริ่มจากค่าว่างเสมอ (ดู startEdit) — ถ้าคลิกเข้ามาแล้วออกโดยไม่พิมพ์อะไร
      // ต้องถือเป็น "ยกเลิก" ไม่ใช่ "ล้างค่าเดิมทิ้ง" (ต่างจากช่องปกติที่ draft ว่าง = ตั้งใจลบค่า
      // เพราะเริ่มจากค่าเดิมอยู่แล้ว รู้ว่าตั้งใจลบจริง)
    } else {
      onSave(draft === '' ? null : draft);
    }
  };

  if (type === 'boolean') {
    return (
      <Box sx={{ py: 0.4 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ width: LABEL_WIDTH, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280' }}>
            {label}
          </Typography>
          <Switch size="small" checked={!!value} onChange={(e) => onSave(e.target.checked)} disabled={disabled} />
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: value ? '#16a34a' : 'text.disabled' }}>
            {displayValue}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (type === 'select') {
    return (
      <Box sx={{ py: 0.4 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ width: LABEL_WIDTH, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280' }}>
            {label}
          </Typography>
          {editing ? (
            <TextField
              select
              size="small"
              fullWidth
              value={value || ''}
              disabled={disabled}
              SelectProps={{
                autoFocus: true,
                open: true,
                onClose: () => setEditing(false),
              }}
              onChange={(e) => {
                setEditing(false);
                onSave(e.target.value || null);
              }}
            >
              <MenuItem value="">ยังไม่ระบุ</MenuItem>
              {(options || []).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          ) : (
            <Box onClick={disabled ? undefined : () => setEditing(true)} sx={valueBoxSx(disabled)}>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8rem', color: displayValue ? 'text.primary' : 'text.disabled' }}
              >
                {displayValue || 'ยังไม่ระบุ'}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0.4 }}>
      <Stack direction="row" alignItems={type === 'textarea' ? 'flex-start' : 'center'} spacing={1}>
        <Typography
          variant="caption"
          sx={{ width: LABEL_WIDTH, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280', pt: type === 'textarea' ? 0.6 : 0 }}
        >
          {label}
        </Typography>
        {editing ? (
          <TextField
            inputRef={inputRef}
            size="small"
            type={type === 'textarea' ? 'text' : type}
            multiline={type === 'textarea'}
            minRows={type === 'textarea' ? 2 : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && type !== 'textarea') {
                e.preventDefault();
                commit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                cancelledRef.current = true;
                commit();
              }
            }}
            fullWidth
            InputLabelProps={type === 'date' ? { shrink: true } : undefined}
            inputProps={{ style: { fontSize: '0.8rem' } }}
          />
        ) : (
          <Box onClick={startEdit} sx={valueBoxSx(disabled)}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: displayValue ? 'text.primary' : 'text.disabled',
              }}
            >
              {displayValue || 'ยังไม่ระบุ'}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// แถวแก้ไขได้แบบเลือกจากรายการค้นหา (ลูกค้า/ผู้รับผิดชอบ) — คลิกค่าเพื่อเปิด Autocomplete
// เลือกแล้วบันทึกทันที (ไม่ต้องกดยืนยันแยก)
export function EditableAutocompleteRow({
  label, displayValue, options, value, inputValue, onInputChange, onChange,
  getOptionLabel = (o) => o?.name || '',
}) {
  const [editing, setEditing] = useState(false);

  return (
    <Box sx={{ py: 0.4 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ width: LABEL_WIDTH, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280' }}>
          {label}
        </Typography>
        {editing ? (
          <Autocomplete
            size="small"
            fullWidth
            options={options}
            value={value}
            inputValue={inputValue}
            onInputChange={(e, v) => onInputChange(v)}
            onChange={(e, v) => {
              onChange(v);
              // ปิดโหมดแก้ไขเฉพาะตอนเลือกค่าจริง — ถ้ากดกากบาทล้างค่าเดิม (v เป็น null) ให้
              // ช่องยังเปิดค้างไว้ให้พิมพ์ค้นหา/เลือกค่าใหม่ต่อได้เลย (เดิมปิดทันทีไม่ว่า v จะ
              // เป็นอะไร ทำให้กดกากบาทแล้วเหมือนไม่มีอะไรเกิดขึ้น เพราะบางฟิลด์ เช่น "ลูกค้า"
              // ปฏิเสธค่า null ใน onChange ของตัวเอง — ไม่มีทางเลือกค่าใหม่ได้เลยถ้าปิดไปก่อน)
              if (v) setEditing(false);
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(o, v2) => (o?._id || o?.id) === (v2?._id || v2?.id)}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} autoFocus onBlur={() => setEditing(false)} inputProps={{ ...params.inputProps, style: { fontSize: '0.8rem' } }} />
            )}
          />
        ) : (
          <Box onClick={() => setEditing(true)} sx={valueBoxSx(false)}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.8rem', color: displayValue ? 'text.primary' : 'text.disabled' }}
            >
              {displayValue || 'ยังไม่ระบุ'}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default Row;
